'use client';

import { Box, Button, Flex, HStack, Text } from '@chakra-ui/react';
import { memo, useCallback, useState } from 'react';
import { LuMonitor } from 'react-icons/lu';

import type { SuggestionCategory } from '@features/assistant/modeSuggestions';

interface CategoryBadgeProps {
  category: SuggestionCategory;
  isActive: boolean;
  onSelect: (id: string) => void;
}

const CategoryBadge = memo(({ category, isActive, onSelect }: CategoryBadgeProps) => {
  const handleClick = useCallback(() => {
    onSelect(category.id);
  }, [category.id, onSelect]);

  const Icon = category.icon;

  return (
    <Button
      onClick={handleClick}
      variant="outline"
      size="sm"
      flexShrink={0}
      borderRadius="full"
      borderColor={isActive ? 'transparent' : 'border.light'}
      bg={isActive ? 'surface.accent' : 'transparent'}
      color={isActive ? 'text.primary' : 'text.secondary'}
      _hover={{ bg: isActive ? 'surface.accent' : 'surface.hover' }}
    >
      <Icon />
      <Text fontSize="sm">{category.label}</Text>
    </Button>
  );
});
CategoryBadge.displayName = 'CategoryBadge';

interface PromptRowProps {
  text: string;
  onSelect: (text: string) => void;
}

const PromptRow = memo(({ text, onSelect }: PromptRowProps) => {
  const handleClick = useCallback(() => {
    onSelect(text);
  }, [text, onSelect]);

  return (
    <Box
      as="button"
      onClick={handleClick}
      textAlign="left"
      w="full"
      px={2}
      py={2}
      borderRadius="md"
      color="text.secondary"
      _hover={{ bg: 'surface.hover', color: 'text.primary' }}
    >
      <Text fontSize="sm">{text}</Text>
    </Box>
  );
});
PromptRow.displayName = 'PromptRow';

interface ModeSuggestionPanelProps {
  panelTitle: string;
  categories: SuggestionCategory[];
  onSelectPrompt: (text: string) => void;
}

const ModeSuggestionPanel = ({
  panelTitle,
  categories,
  onSelectPrompt,
}: ModeSuggestionPanelProps) => {
  const [activeId, setActiveId] = useState<string | null>(null);

  const handleSelectCategory = useCallback((id: string) => {
    setActiveId(id);
  }, []);

  if (categories.length === 0) {
    return null;
  }

  const activeCategory = categories.find((c) => c.id === activeId) ?? categories[0];

  return (
    <Box
      w="full"
      borderWidth="1px"
      borderColor="border.light"
      borderRadius="2xl"
      bg="surface.muted"
      overflow="hidden"
    >
      <Flex alignItems="center" px={4} py={3} gap={2}>
        <Box color="text.secondary">
          <LuMonitor />
        </Box>
        <Text fontSize="sm" fontWeight="medium" color="text.secondary">
          {panelTitle}
        </Text>
      </Flex>
      <HStack
        px={4}
        pb={3}
        gap={2}
        overflowX="auto"
        css={{
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {categories.map((category) => (
          <CategoryBadge
            key={category.id}
            category={category}
            isActive={category.id === activeCategory.id}
            onSelect={handleSelectCategory}
          />
        ))}
      </HStack>
      <Flex direction="column" px={3} pb={3} gap={1}>
        {activeCategory.prompts.map((prompt) => (
          <PromptRow key={prompt.text} text={prompt.text} onSelect={onSelectPrompt} />
        ))}
      </Flex>
    </Box>
  );
};

export default memo(ModeSuggestionPanel);
